import { useState, useEffect, type FormEvent, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Plus } from 'lucide-react';
import { useUpdatePrompt, useUpdatePromptStatus } from '@/api/queries/prompts';
import { useTopics } from '@/api/queries/promptTopics';
import type { Prompt, UpdatePromptDto, PromptStatus } from '@/types/prompt';

interface EditPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: Prompt;
}

export function EditPromptDialog({ open, onOpenChange, prompt }: EditPromptDialogProps) {
  const { t } = useTranslation('prompts');
  const { t: tCommon } = useTranslation('common');
  const statusId = useId();
  const promptIdId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const topicId = useId();
  const promptTemplateId = useId();
  const queryKeywordsId = useId();
  const requiredToolsId = useId();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    topic_id: '',
    prompt_template: '',
    status: 'draft' as PromptStatus,
  });

  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [tools, setTools] = useState<string[]>([]);
  const [toolInput, setToolInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updatePrompt = useUpdatePrompt();
  const updatePromptStatus = useUpdatePromptStatus();
  const { data: topicsData } = useTopics();

  // Initialize form with prompt data
  useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name,
        description: prompt.description,
        topic_id: prompt.topic_id || '',
        prompt_template: prompt.prompt_template,
        status: prompt.status,
      });
      setKeywords(prompt.query_keywords || []);
      setTools(prompt.required_tools || []);
    }
  }, [prompt]);

  const handleClose = () => {
    setErrors({});
    onOpenChange(false);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const addTool = () => {
    if (toolInput.trim() && !tools.includes(toolInput.trim())) {
      setTools([...tools, toolInput.trim()]);
      setToolInput('');
    }
  };

  const removeTool = (tool: string) => {
    setTools(tools.filter(t => t !== tool));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('errors.nameRequired');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('errors.descriptionRequired');
    }
    if (!formData.prompt_template.trim()) {
      newErrors.prompt_template = t('errors.promptTemplateRequired');
    }
    if (!formData.topic_id.trim()) {
      newErrors.topic_id = t('errors.topicRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStatusChange = (newStatus: PromptStatus) => {
    // Update the form state
    setFormData({ ...formData, status: newStatus });

    // Submit status change immediately
    updatePromptStatus.mutate({
      promptId: prompt.prompt_id,
      status: newStatus,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const updateData: UpdatePromptDto = {
      name: formData.name,
      description: formData.description,
      topic_id: formData.topic_id,
      query_keywords: keywords,
      prompt_template: formData.prompt_template,
      required_tools: tools,
      status: formData.status,
    };

    updatePrompt.mutate(
      {
        promptId: prompt.prompt_id,
        data: updateData,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-3xl max-h-[90vh] !flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('editPrompt', { name: prompt.name })}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-6 overflow-y-auto flex-1 p-4">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={promptIdId}>{t('promptId')}</Label>
                  <Input value={prompt.prompt_id} disabled className="bg-gray-50" />
                </div>

                <div>
                  <Label htmlFor={statusId}>{t('status')}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={value => handleStatusChange(value as PromptStatus)}
                  >
                    <SelectTrigger id={statusId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t('draft')}</SelectItem>
                      <SelectItem value="active">{t('active')}</SelectItem>
                      <SelectItem value="archived">{t('archived')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor={nameId}>{t('name')}</Label>
              <Input
                id={nameId}
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('placeholders.name')}
              />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor={descriptionId}>{t('description')}</Label>
              <Textarea
                id={descriptionId}
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('placeholders.description')}
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
              )}
            </div>

            <div>
              <Label htmlFor={topicId}>{t('topic')}</Label>
              <Select
                value={formData.topic_id}
                onValueChange={value => setFormData({ ...formData, topic_id: value })}
              >
                <SelectTrigger id={topicId} className={errors.topic_id ? 'border-red-500' : ''}>
                  <SelectValue placeholder={t('placeholders.selectTopic')} />
                </SelectTrigger>
                <SelectContent>
                  {topicsData?.topics?.map(topic => (
                    <SelectItem key={topic._id} value={topic._id || ''}>
                      {topic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.topic_id && <p className="text-sm text-red-600 mt-1">{errors.topic_id}</p>}
            </div>

            <div>
              <Label htmlFor={promptTemplateId}>{t('promptTemplate')}</Label>
              <Textarea
                id={promptTemplateId}
                value={formData.prompt_template}
                onChange={e => setFormData({ ...formData, prompt_template: e.target.value })}
                placeholder={t('placeholders.promptTemplate')}
                rows={6}
              />
              {errors.prompt_template && (
                <p className="text-sm text-red-600 mt-1">{errors.prompt_template}</p>
              )}
            </div>

            {/* Keywords */}
            <div>
              <Label htmlFor={queryKeywordsId}>{t('queryKeywords')}</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  id={queryKeywordsId}
                  value={keywordInput}
                  onChange={e => setKeywordInput(e.target.value)}
                  placeholder={t('placeholders.addKeyword')}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" onClick={addKeyword} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {keywords.map(keyword => (
                  <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                    {keyword}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeKeyword(keyword)} />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Tools */}
            <div>
              <Label htmlFor={requiredToolsId}>{t('requiredTools')}</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={toolInput}
                  onChange={e => setToolInput(e.target.value)}
                  placeholder={t('placeholders.addTool')}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTool())}
                />
                <Button type="button" onClick={addTool} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tools.map(tool => (
                  <Badge key={tool} variant="outline" className="flex items-center gap-1">
                    {tool}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTool(tool)} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              {tCommon('buttons.cancel')}
            </Button>
            <Button type="submit" disabled={updatePrompt.isPending || updatePromptStatus.isPending}>
              {updatePrompt.isPending || updatePromptStatus.isPending
                ? tCommon('buttons.saving')
                : t('updatePrompt')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
